import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FormContainer from "../shared/FormContainer";
import CustomInput from "../shared/CustomInput";
import Button from "../shared/Button";
import { Api } from "../../network/api";
import { saveToken, updateIsBlocked,saveEssaysAllowed,saveSubscriptionActive,saveIsEssayAllowed } from "../../utils/cookies";
import {
  loginStart,
  loginSuccess,
  loginFailure,
  clearError,
} from "../../redux/slice/userSlice";

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});

  // Clear errors when component mounts
  useEffect(() => {
    dispatch(clearError());
    setErrors({});
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    dispatch(loginStart());
    console.log("Login form data:", formData);
    try {
      const response = await Api.login(formData);
      const data = response?.data;
      console.log("data", data);
      const userAuth = data?.access_token && data?.is_admin === false;
      if (userAuth) {
        saveToken(response?.data?.access_token, rememberMe);
        updateIsBlocked(data?.is_blocked);
        dispatch(loginSuccess(data));
        saveSubscriptionActive(data?.subscription_active); 
        saveIsEssayAllowed(data?.is_essay_allowed);
        saveEssaysAllowed(data?.essays_allowed);
        navigate("/");
      } else if (data?.is_admin === true) {
        dispatch(
          loginFailure("Your are not authorized to access this page as admin")
        );
      } else {
        dispatch(loginFailure("Login failed. Please try again."));
        setErrors({ general: "Login failed. Please try again." });
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage =
        error?.response?.data?.detail || "Login failed. Please try again.";
      dispatch(loginFailure(errorMessage));
      setErrors({ general: errorMessage });
    }
  };

  const handleRegisterClick = () => {
    dispatch(clearError());
    setErrors({});
    navigate("/register");
  };

  return (
    <FormContainer title="Welcome Back" subtitle="Sign in to your account">
      <form onSubmit={handleSubmit} className="space-y-6">
        {(errors.general || error) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-left animate-shake">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {errors.general || error}
            </div>
          </div>
        )}

        <div className="animate-fadeInUp animation-delay-100">
          <CustomInput
            type="email"
            name="email"
            label="Email Address"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
            autoComplete="off"
          />
        </div>

        <div className="animate-fadeInUp animation-delay-200">
          <CustomInput
            type="password"
            name="password"
            label="Password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleInputChange}
            error={errors.password}
            required
            autoComplete="current-password"
          />
        </div>

        <div className="flex items-center justify-between animate-fadeInUp animation-delay-300">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200 hover:scale-110"
            />
            <label
              htmlFor="remember-me"
              className="ml-2 block text-sm text-gray-900 hover:text-gray-700 transition-colors duration-200"
            >
              Remember me
            </label>
          </div>

          <div className="text-sm">
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200 hover:underline"
            >
              Forgot your password?
            </button>
          </div>
        </div>

        <div className="animate-fadeInUp animation-delay-400">
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
            className="w-full hover-lift"
            size="large"
          >
            Sign In
          </Button>
        </div>

        <div className="text-center animate-fadeInUp animation-delay-500">
          <p className="text-sm text-gray-600">
            Don't have an account?{" "}
            <button
              type="button"
              onClick={handleRegisterClick}
              className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200 hover:underline"
            >
              Sign up
            </button>
          </p>
        </div>
      </form>
    </FormContainer>
  );
};

export default LoginForm;
